import { addressFrame, addressSearchText, preferPostcode } from "@shared/address-point.ts";
import { CANNOT_CHANGE_MESSAGE, customerEditBlocked } from "@shared/campaign-rules.ts";
import { zoneSources } from "@shared/mail-rules.ts";
import type { MapView } from "@shared/zone-scope.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { AddressSearch } from "../components/AddressSearch.tsx";
import { FILL_PAGE, OUTLINE_BTN, Pagination, ROW_SCROLL } from "../components/account-ui.tsx";
import { CampaignMap, type DrawTool, type RevealRequest } from "../components/CampaignMap.tsx";
import { ApprovalBanner } from "../components/legacy/ApprovalBanner.tsx";
import { ApproveArtwork } from "../components/legacy/ApproveArtwork.tsx";
import { useAppDm } from "../components/legacy/app-state.tsx";
import { FinaliseCampaign } from "../components/legacy/FinaliseCampaign.tsx";
import { ManageArtwork } from "../components/legacy/ManageArtwork.tsx";
import { PaginatedAddressList, UploadAddresses } from "../components/legacy/UploadAddresses.tsx";
import { LoadingSpinnerWithBackground, Modal, useToast } from "../components/legacy/ui.tsx";
import { ZonePanel } from "../components/ZonePanel.tsx";
import {
  CampaignOutlineIcon,
  CSVIcon,
  DownloadIcon,
  HomeOutlineIcon,
  HomeWorkIcon,
  MapPinIcon,
  MyLocationIcon,
  WorkOutlineIcon,
} from "../icons/index.tsx";
import {
  api,
  type CampaignZone,
  type CustomerAddress,
  type Ring,
  type UsedZone,
  type ZoneAddress,
  type ZoneOption,
  type ZonePreset,
} from "../lib/api.ts";
import { compactCount, formatNumbers, nextZoneName } from "../lib/legacy.ts";
import {
  DEFAULT_SHAPE_METRES,
  locationShapeMetres,
  locationZoneOrigin,
  radiusRing,
  rectangleRing,
  type ShapeDraft,
  shapeDraftSettled,
  shapeSizeOk,
  withShapeDraft,
  zoneForLocation,
} from "../lib/location-shape.ts";
import { useAppConfig } from "./__root.tsx";
import { useAddresses } from "./account.address-book.tsx";

export const Route = createFileRoute("/app/$customerId/mycampaigns/$campaignId")({
  component: CampaignEditor,
  validateSearch: (s: Record<string, unknown>): { panel?: "artwork" | "approval" } =>
    s.panel === "artwork" || s.panel === "approval" ? { panel: s.panel } : {},
});

const LOCKED = new Set(["out_on_proof", "complete", "posted", "cancelled", "rejected", "deleted"]);

function boundsOf(rings: Ring[]): [number, number, number, number] {
  const pts = rings.flat();
  const lngs = pts.map((p) => p[0]);
  const lats = pts.map((p) => p[1]);
  return [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
}

function drawnFrame(zone: CampaignZone): [number, number, number, number] | null {
  if (!zone.rings) return null;
  try {
    const rings = JSON.parse(zone.rings) as Ring[];
    if (rings.length === 0) return null;
    return boundsOf(rings);
  } catch {
    return null;
  }
}

/** Rollup box for a named zone. Null when that code has no box. */
async function zoneBox(
  kind: string,
  code: string,
): Promise<[number, number, number, number] | null> {
  const { zones: found } = await api.get<{ zones: ZoneOption[] }>(
    `/count/zones?kind=${kind}&q=${encodeURIComponent(code)}`,
  );
  const hit = found.find((f) => f.code === code);
  if (hit?.west == null || hit.south == null || hit.east == null || hit.north == null) return null;
  return [hit.west, hit.south, hit.east, hit.north];
}

/** Postcode box for a named zone. Null when the rollup has no box. */
async function namedFrame(zone: CampaignZone): Promise<[number, number, number, number] | null> {
  if (!zone.zone_kind || !zone.zone_code) return null;
  return zoneBox(zone.zone_kind, zone.zone_code);
}

function locationLabel(address: CustomerAddress) {
  const place = address.location_name?.trim();
  if (address.company && place) return `${address.company} - ${place}`;
  return place || address.company || address.full_name || address.street_line1;
}

function frameCentre(box: [number, number, number, number]) {
  return { lng: (box[0] + box[2]) / 2, lat: (box[1] + box[3]) / 2 };
}

const RADIUS_GLYPH = "M12 4a8 8 0 1 0 0 16a8 8 0 1 0 0-16zM12 12h8";
const RECT_GLYPH = "M4 6h16v12H4z";

function ShapeGlyph({ d }: { d: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
      strokeLinecap="round"
    >
      <path d={d} />
    </svg>
  );
}

type ShapeKind = "radius" | "rectangle";

interface ShapeEditor {
  id: string;
  kind: ShapeKind;
  radius: string;
  width: string;
  height: string;
}

/** The point a saved place jumps to: a matched address, or the postcode centre. */
async function placePoint(
  address: CustomerAddress,
): Promise<{ lng: number; lat: number; box: [number, number, number, number] } | null> {
  const q = addressSearchText([
    address.street_line1,
    address.street_line2,
    address.city,
    address.postal_code,
  ]);
  try {
    const { results } = await api.get<{
      results: { postcode: string | null; lng: number; lat: number }[];
    }>(`/search/addresses?q=${encodeURIComponent(q)}`);
    const hit = preferPostcode(results, address.postal_code);
    if (hit) {
      return { lng: hit.lng, lat: hit.lat, box: addressFrame(hit.lng, hit.lat) };
    }
  } catch {
    // The postcode box below is the same fallback the opening frame uses.
  }
  const box = await zoneBox("postcode", address.postal_code);
  if (!box) return null;
  return { ...frameCentre(box), box };
}

/** One saved location jumps straight there. Several open a menu first. */
function LocationTarget({
  zones,
  canDraw,
  onJump,
  onFrame,
  onShape,
  onDraft,
}: {
  zones: CampaignZone[];
  canDraw?: boolean;
  onJump: () => void;
  onFrame: (box: [number, number, number, number], label: string) => void;
  onDraft?(draft: ShapeDraft | null): void;
  onShape?(
    hit: { id: string; lng: number; lat: number; label: string },
    rings: Ring[],
    kind: "circle" | "rectangle",
  ): void;
}) {
  const { data } = useAddresses();
  const { showToast } = useToast();
  const locations = data?.addresses ?? [];
  const many = locations.length > 1;
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const [editor, setEditor] = useState<ShapeEditor | null>(null);
  const [point, setPoint] = useState<{ id: string; lng: number; lat: number } | null>(null);
  const shapes = Boolean(canDraw && onShape);
  const locationsRef = useRef(locations);
  locationsRef.current = locations;

  const close = () => {
    setOpen(false);
    setEditor(null);
  };

  // The point is resolved once per location, so typing a size does not search again.
  const editorId = editor?.id ?? null;
  useEffect(() => {
    if (!editorId) {
      setPoint(null);
      return;
    }
    const address = locationsRef.current.find((item) => item.id === editorId);
    if (!address) return;
    let cancelled = false;
    void placePoint(address).then((found) => {
      if (cancelled || !found) return;
      setPoint({ id: editorId, lng: found.lng, lat: found.lat });
    });
    return () => {
      cancelled = true;
    };
  }, [editorId]);

  useEffect(() => {
    if (!onDraft) return;
    if (!editor || !point || point.id !== editor.id) {
      onDraft(null);
      return;
    }
    // An empty or out-of-range size leaves the last outline where it is.
    const kind = editor.kind === "radius" ? "circle" : "rectangle";
    const metres =
      editor.kind === "radius"
        ? [Number(editor.radius)]
        : [Number(editor.width), Number(editor.height)];
    if (!metres.every((value) => shapeSizeOk(value))) return;
    const ring =
      kind === "circle"
        ? radiusRing(point.lng, point.lat, metres[0] ?? 0)
        : rectangleRing(point.lng, point.lat, metres[0] ?? 0, metres[1] ?? 0);
    onDraft({ addressId: editor.id, kind, rings: [ring] });
  }, [editor, onDraft, point]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (menuRef.current?.contains(event.target as Node)) return;
      setOpen(false);
      setEditor(null);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      setEditor(null);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  async function choose(address: CustomerAddress) {
    close();
    const found = await placePoint(address);
    if (!found) {
      showToast("That postcode has no map area", "error");
      return;
    }
    onFrame(found.box, `Org Location, ${address.city}`);
  }

  function toggle(address: CustomerAddress, kind: ShapeKind) {
    setEditor((current) => {
      if (current?.id === address.id && current.kind === kind) return null;
      const shape = kind === "radius" ? "circle" : "rectangle";
      const size = locationShapeMetres(zones, address.id, shape);
      const fallback = String(DEFAULT_SHAPE_METRES);
      return {
        id: address.id,
        kind,
        radius: size && "radius" in size ? String(size.radius) : fallback,
        width: size && "width" in size ? String(size.width) : fallback,
        height: size && "height" in size ? String(size.height) : fallback,
      };
    });
  }

  async function commit(address: CustomerAddress) {
    if (!editor || editor.id !== address.id || !onShape) return;
    const found =
      point?.id === address.id ? { lng: point.lng, lat: point.lat } : await placePoint(address);
    if (!found) {
      showToast("That postcode has no map area", "error");
      return;
    }
    const ring =
      editor.kind === "radius"
        ? radiusRing(found.lng, found.lat, Number(editor.radius))
        : rectangleRing(found.lng, found.lat, Number(editor.width), Number(editor.height));
    onShape(
      { id: address.id, lng: found.lng, lat: found.lat, label: locationLabel(address) },
      [ring],
      editor.kind === "radius" ? "circle" : "rectangle",
    );
    close();
  }

  return (
    <div className="relative z-20 shrink-0" ref={menuRef}>
      <button
        type="button"
        aria-label="Go to my location"
        aria-haspopup={many ? "menu" : undefined}
        aria-expanded={many ? open : undefined}
        aria-controls={many ? menuId : undefined}
        className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-[#D71635] shadow-sm"
        onClick={() => (many ? setOpen((value) => !value) : onJump())}
      >
        <MyLocationIcon />
      </button>
      {many && open && (
        <div
          id={menuId}
          role="menu"
          className="absolute left-0 z-40 mt-1 w-max min-w-64 max-w-lg rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {locations.map((address) => {
            const editing = editor?.id === address.id ? editor : null;
            const sizes =
              editing?.kind === "radius"
                ? [editing.radius]
                : [editing?.width ?? "", editing?.height ?? ""];
            const ready = Boolean(editing) && sizes.every((value) => shapeSizeOk(Number(value)));
            const label = locationLabel(address);
            return (
              <div key={address.id}>
                <div className="flex items-center gap-1 pr-2">
                  <button
                    type="button"
                    role="menuitem"
                    className="min-w-0 px-4 py-2.5 text-left hover:bg-gray-50"
                    onClick={() => choose(address)}
                  >
                    <span className="block truncate text-sm font-medium text-gray-900">
                      {label}
                    </span>
                    <span className="block truncate text-sm text-gray-500">
                      {address.city} {address.postal_code}
                    </span>
                  </button>
                  <span className="min-w-0 flex-1" />
                  {shapes && (
                    <>
                      <button
                        type="button"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 shadow-sm hover:bg-gray-50"
                        aria-label={`Radius around ${label}`}
                        aria-expanded={editing?.kind === "radius"}
                        onClick={() => toggle(address, "radius")}
                      >
                        <ShapeGlyph d={RADIUS_GLYPH} />
                      </button>
                      <button
                        type="button"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 shadow-sm hover:bg-gray-50"
                        aria-label={`Rectangle around ${label}`}
                        aria-expanded={editing?.kind === "rectangle"}
                        onClick={() => toggle(address, "rectangle")}
                      >
                        <ShapeGlyph d={RECT_GLYPH} />
                      </button>
                    </>
                  )}
                </div>
                {editing && (
                  <form
                    className="flex flex-wrap items-center gap-2 px-3 pb-2 text-xs text-gray-700"
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (ready) void commit(address);
                    }}
                  >
                    {editing.kind === "radius" ? (
                      <label className="flex items-center gap-1">
                        Radius
                        <input
                          type="number"
                          min={1}
                          max={20_000}
                          inputMode="decimal"
                          aria-label="Radius in metres"
                          className="w-16 rounded border border-gray-300 px-1.5 py-1"
                          value={editing.radius}
                          onChange={(event) =>
                            setEditor({ ...editing, radius: event.target.value })
                          }
                        />
                        m
                      </label>
                    ) : (
                      <>
                        <label className="flex items-center gap-1">
                          Width
                          <input
                            type="number"
                            min={1}
                            max={20_000}
                            inputMode="decimal"
                            aria-label="Width in metres"
                            className="w-16 rounded border border-gray-300 px-1.5 py-1"
                            value={editing.width}
                            onChange={(event) =>
                              setEditor({ ...editing, width: event.target.value })
                            }
                          />
                          m
                        </label>
                        <label className="flex items-center gap-1">
                          Height
                          <input
                            type="number"
                            min={1}
                            max={20_000}
                            inputMode="decimal"
                            aria-label="Height in metres"
                            className="w-16 rounded border border-gray-300 px-1.5 py-1"
                            value={editing.height}
                            onChange={(event) =>
                              setEditor({ ...editing, height: event.target.value })
                            }
                          />
                          m
                        </label>
                      </>
                    )}
                    <button
                      type="submit"
                      disabled={!ready}
                      className="rounded bg-primary-600 px-2 py-1 font-semibold text-white disabled:opacity-40"
                    >
                      Set
                    </button>
                  </form>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** The parse job writes counted_at after the upload response. */
const PARSE_WATCH_MS = 3 * 60_000;

/**
 * Keep asking for the campaign until counted_at moves.
 * ponytail: stops at the first change, or after 3 minutes if the job never
 * finishes. Two files uploaded before either job runs share that one change;
 * a later upload starts a new watch. A per-upload job id would split them.
 */
function useParseWatch(campaignId: string, countedAt: number | null) {
  const queryClient = useQueryClient();
  const baseline = useRef<number | null>(null);
  const until = useRef(0);
  const [armed, setArmed] = useState(0);

  const arm = useCallback(() => {
    baseline.current = countedAt;
    until.current = Date.now() + PARSE_WATCH_MS;
    setArmed((n) => n + 1);
  }, [countedAt]);

  useEffect(() => {
    if (armed === 0) return;
    if (countedAt !== baseline.current || Date.now() >= until.current) return;
    const timer = window.setInterval(() => {
      if (Date.now() >= until.current) {
        window.clearInterval(timer);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
      void queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      void queryClient.invalidateQueries({ queryKey: ["campaign-addresses", campaignId] });
    }, 3000);
    return () => window.clearInterval(timer);
  }, [armed, countedAt, campaignId, queryClient]);

  return arm;
}

function CampaignEditor() {
  const { campaignId, customerId } = Route.useParams();
  const { panel } = Route.useSearch();
  const config = useAppConfig();
  const dm = useAppDm();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { ui, set, campaign, zones, tiers } = dm;

  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [tool, setTool] = useState<DrawTool | null>(null);
  const doneRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!campaignId) return;
    setTool(null);
  }, [campaignId]);
  useEffect(() => {
    if (!campaign || zoneSources(campaign.mail_type).draw) return;
    setTool(null);
  }, [campaign]);
  const [fitBounds, setFitBounds] = useState<[number, number, number, number] | null>(null);
  const [target, setTarget] = useState<{ lng: number; lat: number; label: string } | null>(null);
  const [shapeDraft, setShapeDraft] = useState<ShapeDraft | null>(null);
  const [heldDraft, setHeldDraft] = useState<ShapeDraft | null>(null);
  useEffect(() => {
    if (heldDraft && shapeDraftSettled(zones, heldDraft)) setHeldDraft(null);
  }, [zones, heldDraft]);
  const draft = shapeDraft ?? heldDraft;
  const mapZones = useMemo(() => (draft ? withShapeDraft(zones, draft) : zones), [zones, draft]);
  // /config may already be refetching (Use Address Location). The map is built
  // once from start, so a new campaign waits for that fetch. Only flips to ready.
  const [mapStartReady, setMapStartReady] = useState(() => !config.isFetching);
  useEffect(() => {
    if (!config.isFetching) setMapStartReady(true);
  }, [config.isFetching]);
  // The dot is the opening target. A menu choice replaces it; this does not
  // put it back, and a zone frame does not move it.
  useEffect(() => {
    if (!mapStartReady) return;
    const box = config.data?.mapStart;
    if (!box) return;
    setTarget(
      (current) =>
        current ?? {
          ...frameCentre(box),
          label: config.data?.mapStartKind === "org" ? "Org Location" : "Geo Location",
        },
    );
  }, [config.data, mapStartReady]);
  const [revealBounds, setRevealBounds] = useState<RevealRequest | null>(null);
  const revealN = useRef(0);
  const [addressesModal, setAddressesModal] = useState<{ zone: CampaignZone; page: number } | null>(
    null,
  );
  const [showSampleCsv, setShowSampleCsv] = useState(false);
  const [addressRefresh, setAddressRefresh] = useState(0);
  const armParseWatch = useParseWatch(campaignId, campaign?.counted_at ?? null);
  const [mapView, setMapView] = useState<MapView | null>(null);

  // Land on the panel the campaign's data source implies, unless the account
  // rail asked for artwork. Applied once per campaign+panel so the sidebar
  // can still switch panels afterwards.
  const openedPanel = useRef<string | null>(null);
  useEffect(() => {
    if (!campaign) return;
    if (panel === "artwork" || panel === "approval") {
      const key = `${campaign.id}:${panel}`;
      if (openedPanel.current === key) return;
      openedPanel.current = key;
      set({
        appContext: dm.adminMode ? "approveArtwork" : "manageArtwork",
        showAddressList: false,
      });
      return;
    }
    if (
      ui.appContext === "myCampaigns" ||
      ui.appContext === "customers" ||
      ui.appContext === "orders"
    ) {
      set({
        appContext: campaign.source === "csv" ? "uploadAddressCSV" : "map",
        showAddressList: campaign.source === "csv",
      });
    }
  }, [campaign, panel, ui.appContext, set, dm.adminMode]);

  const customerBlocked = campaign
    ? !dm.isCurrentAdmin && customerEditBlocked(campaign.status, campaign.order_status)
    : false;
  const locked = campaign ? LOCKED.has(campaign.status) || customerBlocked : false;

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] }),
      queryClient.invalidateQueries({ queryKey: ["campaigns"] }),
    ]);

  const addZone = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post(`/campaigns/${campaignId}/zones`, body),
    onSuccess: async () => {
      await invalidate();
      showToast("Zone added", "success");
    },
    onError: (e: Error) => {
      setHeldDraft(null);
      showToast(e.message, "error");
    },
  });
  const removeZone = useMutation({
    mutationFn: (zoneId: string) => api.delete(`/campaigns/${campaignId}/zones/${zoneId}`),
    onSuccess: () => {
      invalidate();
      showToast("Polygon deleted successfully!", "success");
    },
    onError: () => showToast("Error deleting Zone!", "error"),
  });
  const renameZone = useMutation({
    mutationFn: ({ zoneId, label }: { zoneId: string; label: string }) =>
      api.patch(`/campaigns/${campaignId}/zones/${zoneId}`, { label }),
    onSuccess: () => {
      invalidate();
      showToast("Polygon name updated successfully!", "success");
    },
    onError: () => showToast("Error updating polygon name!", "error"),
  });
  const updateZoneRings = useMutation({
    mutationFn: async ({
      zoneId,
      rings,
      origin,
      kind,
      dropIds = [],
    }: {
      zoneId: string;
      rings: Ring[];
      origin?: string;
      kind?: "circle" | "rectangle";
      dropIds?: string[];
    }) => {
      await Promise.all(dropIds.map((id) => api.delete(`/campaigns/${campaignId}/zones/${id}`)));
      return api.patch(`/campaigns/${campaignId}/zones/${zoneId}`, {
        rings,
        ...(origin ? { origin } : {}),
        ...(kind ? { kind } : {}),
      });
    },
    onSuccess: () => invalidate(),
    onError: (e: Error) => {
      setHeldDraft(null);
      showToast(e.message, "error");
    },
  });

  const firstZone = zones[0] ?? null;
  const firstDrawn = firstZone ? drawnFrame(firstZone) : null;
  const namedBox = useQuery({
    queryKey: ["zone-frame", firstZone?.id, firstZone?.zone_kind, firstZone?.zone_code],
    enabled: Boolean(firstZone && !firstDrawn && firstZone.zone_kind && firstZone.zone_code),
    queryFn: () => namedFrame(firstZone!),
  });

  const unitCents = useMemo(() => {
    if (!campaign?.product_id) return 0;
    let chosen = 0;
    for (const t of tiers
      .filter((t) => t.product_id === campaign.product_id)
      .sort((a, b) => a.min_qty - b.min_qty)) {
      if (campaign.address_count >= t.min_qty) chosen = t.unit_cents;
    }
    return chosen;
  }, [campaign, tiers]);

  if (!campaign) {
    return dm.loading ? (
      <LoadingSpinnerWithBackground />
    ) : (
      <div className="new-route p-8">
        <h1>Campaign Not Found</h1>
        <p>No campaign found with the specified ID.</p>
      </div>
    );
  }

  const audience = campaign.audience ?? "both";
  const showBusiness = audience !== "residential";
  const showResidential = audience !== "business";
  const countIcon = !showResidential ? (
    <WorkOutlineIcon />
  ) : !showBusiness ? (
    <HomeOutlineIcon />
  ) : (
    <HomeWorkIcon />
  );
  const zoneCount = (z: { res_count: number; biz_count: number }) =>
    audience === "business"
      ? z.biz_count
      : audience === "residential"
        ? z.res_count
        : z.res_count + z.biz_count;
  const campaignCount = campaign.address_count;

  const setAudience = (which: "business" | "residential") => {
    const nextBusiness = which === "business" ? !showBusiness : showBusiness;
    const nextResidential = which === "residential" ? !showResidential : showResidential;
    if (!nextBusiness && !nextResidential) return;
    void dm.updateCampaign({
      audience:
        nextBusiness && nextResidential ? "both" : nextBusiness ? "business" : "residential",
    });
  };

  const inDataContext = ui.appContext === "map" || ui.appContext === "uploadAddressCSV";
  const fillAddresses = ui.appContext === "uploadAddressCSV" && ui.showAddressList;
  const zoomTo = async (z: CampaignZone) => {
    const box = drawnFrame(z) ?? (await namedFrame(z));
    if (box) setFitBounds(box);
  };

  // A campaign that already has zones opens on the first one. A named zone
  // waits for its box so the map is not built on the user and then moved.
  const zoneFrame = firstDrawn ?? namedBox.data ?? null;
  const frameReady =
    !firstZone ||
    firstDrawn !== null ||
    !(firstZone.zone_kind && firstZone.zone_code) ||
    namedBox.isFetched;
  const mapOpen = firstZone
    ? (zoneFrame ?? (frameReady ? (config.data?.mapStart ?? null) : null))
    : (config.data?.mapStart ?? null);

  return (
    <div className={fillAddresses ? FILL_PAGE : "flex flex-col"}>
      <div className={fillAddresses ? "flex min-h-0 min-w-0 flex-1 flex-col" : "w-full"}>
        {(addZone.isPending || updateZoneRings.isPending) && (
          <LoadingSpinnerWithBackground
            message={
              updateZoneRings.isPending
                ? "Please wait while the zone is being updated"
                : "Please wait while the zone is being added"
            }
          />
        )}

        {addressesModal && (
          <ZoneAddressesModal
            campaignId={campaignId}
            zone={addressesModal.zone}
            page={addressesModal.page}
            setPage={(page) => setAddressesModal({ ...addressesModal, page })}
            onClose={() => setAddressesModal(null)}
          />
        )}

        <section
          className={
            fillAddresses ? "flex min-h-0 w-full flex-1 flex-col gap-3" : "w-full space-y-3"
          }
        >
          {inDataContext && (
            <div className="flex shrink-0 flex-wrap items-center gap-3">
              <span className="flex flex-wrap items-center gap-2">
                {ui.showAddressList && campaign.mail_type === "addressed" && (
                  <button
                    type="button"
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                    onClick={() => set({ uploadExclusionFiles: !ui.uploadExclusionFiles })}
                  >
                    {ui.uploadExclusionFiles ? "Upload Mail To List" : "Upload Do Not Mail List"}
                  </button>
                )}
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
                  onClick={async () => {
                    const nextSource = ui.showAddressList ? "map" : "csv";
                    if (campaign.source !== nextSource && customerBlocked) {
                      dm.notice("Campaign", CANNOT_CHANGE_MESSAGE);
                      return;
                    }
                    if (ui.showAddressList) {
                      set({ showAddressList: false, appContext: "map" });
                      if (campaign.source !== "map") await dm.updateCampaign({ source: "map" });
                    } else {
                      set({ showAddressList: true, appContext: "uploadAddressCSV" });
                      if (campaign.source !== "csv")
                        await dm.updateCampaign({ source: "csv", mailType: "addressed" });
                    }
                  }}
                >
                  {!ui.showAddressList ? (
                    <>
                      Show Address List
                      <HomeWorkIcon className="h-5 w-5 fill-current" />
                    </>
                  ) : (
                    <>
                      Show Map
                      <MapPinIcon className="h-5 w-5" />
                    </>
                  )}
                </button>

                {ui.showAddressList && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
                    onClick={() => setShowSampleCsv(true)}
                  >
                    <CSVIcon width={20} height={20} />
                    Download Sample CSV
                  </button>
                )}
                <Modal
                  open={showSampleCsv}
                  title="Download CSV"
                  submitText="Done"
                  onSubmit={() => setShowSampleCsv(false)}
                  icon={<CampaignOutlineIcon />}
                >
                  <div className="space-y-4">
                    <div>
                      <p className="">Select a sample CSV file to download…</p>
                      <ul className="mb-8 divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
                        <li>
                          <a
                            className="flex items-center gap-3 px-4 py-3 text-sm text-gray-900 hover:bg-gray-50"
                            href="/sample-files/econnect_dm_sample_address.csv"
                            download
                          >
                            <CSVIcon width={22} height={22} />
                            <span className="mr-auto">
                              Blank Sample Address CSV File (header only)
                            </span>
                            <DownloadIcon width={22} height={22} />
                          </a>
                        </li>
                        <li>
                          <a
                            className="flex items-center gap-3 px-4 py-3 text-sm text-gray-900 hover:bg-gray-50"
                            href="/sample-files/econnect_dm_with_sample_addresses.csv"
                            download
                          >
                            <CSVIcon width={22} height={22} />
                            <span className="mr-auto">Sample CSV File with Address Examples</span>
                            <DownloadIcon width={22} height={22} />
                          </a>
                        </li>
                      </ul>
                      <p className="mb-2 text-sm">
                        Note: Fields marked with an asterisk are required, these include the
                        following…
                      </p>
                      <ul className="bullets text-sm">
                        <li>
                          streetAddress<sup className="text-red-700">*</sup>
                        </li>
                        <li>
                          suburb<sup className="text-red-700">*</sup>
                        </li>
                        <li>
                          state<sup className="text-red-700">*</sup>
                        </li>
                        <li>
                          postalCode<sup className="text-red-700">*</sup>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Modal>
              </span>
              <span className="ml-auto flex items-center gap-2">
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-gray-100 px-3 py-1 text-sm font-semibold tabular-nums text-gray-800"
                  title={`${formatNumbers(campaignCount) || "0"} addresses`}
                >
                  {compactCount(campaignCount)}
                  <span className="text-gray-500 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:fill-current">
                    {countIcon}
                  </span>
                </span>
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-sm font-semibold tabular-nums text-emerald-800"
                  title="Estimated print cost"
                >
                  <span className="text-xs font-medium text-emerald-700">Est.</span>$
                  {formatNumbers(Math.round((campaign.address_count * unitCents) / 100))}
                </span>
              </span>
            </div>
          )}

          {ui.appContext !== "manageArtwork" &&
            ui.appContext !== "approveArtwork" &&
            ui.appContext !== "confirmCampaign" && (
              <div className="shrink-0">
                <ApprovalBanner />
              </div>
            )}
          {ui.appContext === "manageArtwork" && <ManageArtwork />}
          {ui.appContext === "approveArtwork" && <ApproveArtwork />}
          {ui.appContext === "confirmCampaign" && <FinaliseCampaign />}

          {ui.appContext === "map" && !ui.showAddressList && (
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
              <div className="min-w-0 w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm lg:flex-1">
                <div className="map-search relative z-10 flex items-center gap-2 border-b border-gray-100 px-4 py-3">
                  <LocationTarget
                    zones={zones}
                    canDraw={zoneSources(campaign.mail_type).draw && !locked}
                    onJump={() => {
                      if (!mapStartReady) return;
                      const box = config.data?.mapStart;
                      if (!box) return;
                      setFitBounds([box[0], box[1], box[2], box[3]]);
                      setTarget({
                        ...frameCentre(box),
                        label:
                          config.data?.mapStartKind === "org" ? "Org Location" : "Geo Location",
                      });
                    }}
                    onFrame={(box, label) => {
                      setFitBounds(box);
                      setTarget({ ...frameCentre(box), label });
                    }}
                    onDraft={setShapeDraft}
                    onShape={(hit, rings, kind) => {
                      setHeldDraft({ addressId: hit.id, kind, rings });
                      setFitBounds(boundsOf(rings));
                      setTarget({ lng: hit.lng, lat: hit.lat, label: hit.label });
                      const origin = locationZoneOrigin(hit.id);
                      const { keep, drop } = zoneForLocation(zones, hit.id, kind, hit);
                      if (keep) {
                        updateZoneRings.mutate({
                          zoneId: keep.id,
                          rings,
                          origin,
                          kind,
                          dropIds: drop.map((zone) => zone.id),
                        });
                      } else {
                        addZone.mutate({
                          kind,
                          label: nextZoneName(zones),
                          rings,
                          origin,
                        });
                      }
                      if (
                        campaign.mail_type !== "unaddressed" &&
                        campaign.selection !== "polygon"
                      ) {
                        void dm.updateCampaign({ selection: "polygon" });
                      }
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <AddressSearch
                      canDraw={zoneSources(campaign.mail_type).draw && !locked}
                      onPick={(hit) => {
                        const pad = 0.004;
                        setFitBounds([hit.lng - pad, hit.lat - pad, hit.lng + pad, hit.lat + pad]);
                      }}
                      onShape={(hit, rings, kind) => {
                        setFitBounds(boundsOf(rings));
                        setTarget({ lng: hit.lng, lat: hit.lat, label: hit.label });
                        addZone.mutate({
                          kind,
                          label: nextZoneName(zones),
                          rings,
                        });
                        if (
                          campaign.mail_type !== "unaddressed" &&
                          campaign.selection !== "polygon"
                        ) {
                          void dm.updateCampaign({ selection: "polygon" });
                        }
                      }}
                    />
                  </div>
                </div>
                <section id="map" className="relative h-[750px] overflow-hidden">
                  {frameReady && (zoneFrame !== null || mapStartReady) && (
                    <CampaignMap
                      zones={mapZones}
                      selectedId={selectedZoneId}
                      onSelect={setSelectedZoneId}
                      onCreate={(kind, rings) =>
                        addZone.mutateAsync({
                          kind: kind === "freehand" ? "polygon" : kind,
                          label: nextZoneName(zones),
                          rings,
                        })
                      }
                      onUpdate={(zoneId, rings) => updateZoneRings.mutate({ zoneId, rings })}
                      onDelete={(zoneId) => removeZone.mutate(zoneId)}
                      counts={Object.fromEntries(zones.map((z) => [z.id, zoneCount(z)]))}
                      readOnly={locked}
                      tool={tool}
                      onToolChange={setTool}
                      doneRef={doneRef}
                      fitBounds={fitBounds}
                      revealBounds={revealBounds}
                      target={target}
                      start={mapOpen}
                      onView={setMapView}
                    />
                  )}
                </section>
              </div>

              <ZonePanel
                campaign={campaign}
                zones={zones}
                mapView={mapView}
                locked={locked}
                tool={tool}
                onTool={(next) => {
                  setTool(next);
                  if (
                    next &&
                    campaign.mail_type !== "unaddressed" &&
                    campaign.selection !== "polygon"
                  ) {
                    void dm.updateCampaign({ selection: "polygon" });
                  }
                }}
                onDone={() => doneRef.current?.()}
                electoralLevel={ui.electoralLevel}
                electoralSubType={ui.electoralSubType}
                onElectoralLevel={(electoralLevel) => set({ electoralLevel })}
                onElectoralSubType={(electoralSubType) => set({ electoralSubType })}
                onPatch={(patch) => void dm.updateCampaign(patch)}
                onAddNamed={(z) => {
                  if (z.west !== null && z.south !== null && z.east !== null && z.north !== null) {
                    setFitBounds([z.west, z.south, z.east, z.north]);
                  }
                  addZone.mutate({
                    kind: "named",
                    label: `${z.name} (${z.code})`,
                    zoneKind: z.kind,
                    zoneCode: z.code,
                  });
                }}
                onAddPreset={(preset: ZonePreset) => {
                  const rings = JSON.parse(preset.rings) as Ring[];
                  setFitBounds(boundsOf(rings));
                  addZone.mutate({ kind: preset.kind, label: preset.name, rings });
                  if (campaign.selection !== "polygon") {
                    void dm.updateCampaign({ selection: "polygon" });
                  }
                }}
                onAddUsed={(zone: UsedZone) => {
                  if (zone.kind !== "named" && zone.rings) {
                    const rings = JSON.parse(zone.rings) as Ring[];
                    setFitBounds(boundsOf(rings));
                    addZone.mutate({
                      kind: zone.kind,
                      label: zone.label ?? undefined,
                      rings,
                      exclude: zone.exclude === 1,
                    });
                    if (campaign.selection !== "polygon") {
                      void dm.updateCampaign({ selection: "polygon" });
                    }
                    return;
                  }
                  if (!zone.zone_kind || !zone.zone_code) return;
                  void zoneBox(zone.zone_kind, zone.zone_code).then((box) => {
                    if (box) setFitBounds(box);
                  });
                  addZone.mutate({
                    kind: "named",
                    label: zone.label ?? undefined,
                    zoneKind: zone.zone_kind,
                    zoneCode: zone.zone_code,
                    exclude: zone.exclude === 1,
                  });
                }}
                customerId={customerId}
                selectedZoneId={selectedZoneId}
                onSelectZone={(z) => {
                  setSelectedZoneId(z.id);
                  if (!z.rings) {
                    void zoomTo(z);
                    return;
                  }
                  revealN.current += 1;
                  setRevealBounds({
                    bounds: boundsOf(JSON.parse(z.rings) as Ring[]),
                    n: revealN.current,
                  });
                }}
                onRename={(zoneId, label) => renameZone.mutate({ zoneId, label })}
                onZoom={(z) => {
                  setSelectedZoneId(z.id);
                  void zoomTo(z);
                }}
                onDelete={(zoneId) => removeZone.mutate(zoneId)}
                onShowAddresses={(z) => setAddressesModal({ zone: z, page: 0 })}
                zoneCount={zoneCount}
                countIcon={countIcon}
                showBusiness={showBusiness}
                showResidential={showResidential}
                onAudience={setAudience}
              />
            </div>
          )}

          {ui.appContext === "uploadAddressCSV" && (
            <UploadAddresses
              campaignId={campaignId}
              uploadExclusionFiles={ui.uploadExclusionFiles}
              disabled={locked}
              onUploadFinish={() => {
                setAddressRefresh((n) => n + 1);
                invalidate();
                armParseWatch();
              }}
            />
          )}
          {ui.appContext === "uploadAddressCSV" && campaign.address_count > 0 && (
            <div className="flex shrink-0 justify-end">
              <a className={OUTLINE_BTN} href={`/api/uploads/${campaignId}/addresses.csv`} download>
                <CSVIcon width={18} height={18} />
                Download Address List as CSV
                <DownloadIcon width={18} height={18} />
              </a>
            </div>
          )}
          {ui.showAddressList &&
            ui.appContext !== "manageArtwork" &&
            ui.appContext !== "approveArtwork" && (
              <PaginatedAddressList campaignId={campaignId} refreshTrigger={addressRefresh} />
            )}
        </section>
      </div>
    </div>
  );
}

// --------------------------------------------------- zone addresses modal

interface ZoneAddressesModalProps {
  campaignId: string;
  zone: CampaignZone;
  page: number;
  setPage: (p: number) => void;
  onClose: () => void;
}

function ZoneAddressesModal({ campaignId, zone, page, setPage, onClose }: ZoneAddressesModalProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["zone-addresses", campaignId, zone.id, page],
    queryFn: () =>
      api.get<{ addresses: ZoneAddress[]; hasMore: boolean; pageSize: number }>(
        `/campaigns/${campaignId}/zones/${zone.id}/addresses?page=${page}`,
      ),
    placeholderData: (prev) => prev,
  });
  const total = zone.res_count + zone.biz_count;
  const perPage = data?.pageSize ?? 100;
  return (
    <Modal
      open
      title="Addresses in selected polygon"
      submitText="Ok"
      onCancel={onClose}
      onSubmit={onClose}
    >
      <div className="space-y-4">
        {isLoading && <div>Loading addresses...</div>}
        {error && <div className="text-red-600">Error fetching addresses!</div>}
        {!error && !isLoading && data?.addresses.length === 0 && (
          <div>No addresses found in the selected polygon.</div>
        )}
        <div className="flex max-h-[calc(100dvh-12rem)] min-h-0 flex-col">
          <div
            className={`${ROW_SCROLL} cursor-not-allowed`}
            style={{ userSelect: "none" }}
            onCopy={(e) => e.preventDefault()}
          >
            {!error &&
              data?.addresses.map((a) => (
                <div className="address-row" key={a.id}>
                  {a.label}
                </div>
              ))}
          </div>
          {!error && !isLoading && (
            <Pagination page={page} pageSize={perPage} total={total} onPage={setPage} />
          )}
        </div>
      </div>
    </Modal>
  );
}
